import React, { useCallback, useRef, useState } from 'react';
import {
  Check,
  Video,
  BookOpen,
  Headphones,
  CalendarCheck,
  MessageSquareQuote,
  Calendar,
  FileText,
  Tag,
  CreditCard,
  ScanFace,
  IdCard,
  ShieldAlert,
  Car,
  Ban,
  ReceiptText,
  TriangleAlert,
  CarFront,
  Radio,
  MessageSquare,
  MessagesSquare,
  ClipboardList,
  LifeBuoy,
  Banknote,
  Landmark,
  ShieldCheck,
  CalendarClock,
  Key,
  Users,
  Utensils,
  Package,
  Globe,
  UserCheck,
  User,
  Search,
  Camera,
  BadgeCheck,
  VolumeX,
  Volume2,
} from 'lucide-react';
import { BRAND_GREEN, IMG, SHARED_IMG, ShimmerCta } from './shared';
import './FeatureShowcase.css';

// RentShield brand palette, inlined from the v0 rentshield/brand module.
const CORAL = '#F52C52';
const GREEN = '#16B981';
const NAVY = '#0E1A38';

// Product walkthrough clips, shared with the /turo-alternative page
// (see src/Component/TuroAlternative/ProductBlocks.jsx).
const RENTDIRECT_VIDEO =
  'https://ti6mkfot02n3tn3d.public.blob.vercel-storage.com/media/rentdirect-demo-hq.mp4';
const RENTID_VIDEO =
  'https://ti6mkfot02n3tn3d.public.blob.vercel-storage.com/media/rentid-demo-hq.mp4';

const FEATURES = [
  {
    id: 'book',
    label: 'Rent',
    title: 'Booking engine and fleet management\n',
    subcopy:
      'Easily offer vehicles to renters with a white-labeled rental website, manage bookings and your fleet from one connected platform.',
    logo: `${IMG}/renter/logos/rentdirect.png`,
    logoAlt: 'rentdirect',
  },
  {
    id: 'verify',
    label: 'Verify',
    title: 'Identify risk for every rental, anytime you want',
    subcopy:
      'Trust and safety, built in. Our AI-powered verification engine screens every driver before a rental, helping you make faster approval decisions, identify higher-risk drivers, and apply consistent checks that protect your fleet.',
    logo: `${IMG}/renter/logos/rentid.png`,
    logoAlt: 'rentID',
  },
  {
    id: 'insure',
    label: 'Insure',
    title: 'Off rent and on rent insurance coverage',
    subcopy:
      'Embedded primary, A-rated commercial auto insurance for private rentals, rideshare, and gig use. Coverage for our business, vehicle, and driver, on and off rent.',
    logo: `${IMG}/renter/logos/rentshield.png`,
    logoAlt: 'rentshield',
  },
  {
    id: 'manage',
    label: 'Automate',
    title: 'Automated rental back-office',
    subcopy:
      'AI rental agents and custom rental workflows activate together to automate your operations, logistics, and administrative daily tasks, saving valuable resources. ',
    logo: `${IMG}/renter/logos/rentai.png`,
    logoAlt: 'rentAI',
  },
  {
    id: 'pay',
    label: 'Pay',
    title: 'Flexible payment methods for modern renters.',
    subcopy:
      'Accept deposits, pre-authorizations, upfront payments, and flexible payment methods, online or at the counter, so renters can pay using the options they already know and trust.',
    logo: '',
    logoAlt: 'rentpay',
  },
  {
    id: 'claim',
    label: 'Resolve',
    title: 'Resolution center for administrative and incident claims',
    subcopy:
      'Manage incidents, claims, refunds, and administrative tasks in one place, giving operators a clear workflow to document issues, organize evidence, track progress, and move each case toward resolution.',
    logo: `${IMG}/renter/logos/rentsuccess.png`,
    logoAlt: 'rentsuccess',
  },
  {
    id: 'educate',
    label: 'Grow',
    title: 'Ongoing education, experts ready to assist your rental journey.',
    subcopy:
      'Learn from experts across rental and insurance. Plus a growing network of coaches, trainers, and mentors ready with practical guidance from people who have built, operated, and grown businesses in the industry.',
    logo: `${IMG}/renter/logos/rentacademy.png`,
    logoAlt: 'rentacademy',
  },
];

/* ---------- Animated list visual (items reveal one at a time) ---------- */

const ListVisual = ({ heading, items }) => (
  <div className="ilp-fs-list">
    <div className="ilp-step ilp-fs-list-head" style={{ animationDelay: '0.05s' }}>
      {heading}
    </div>
    <div className="ilp-fs-list-grid">
      {items.map((item, i) => (
        <div
          key={item.label}
          className="ilp-step ilp-fs-list-item"
          style={{ animationDelay: `${0.2 + i * 0.18}s` }}
        >
          <span className="ilp-fs-list-icon">
            <item.icon className="ilp-fs-icon-14" />
          </span>
          <span className="ilp-fs-list-label">{item.label}</span>
          {item.badge && <span className="ilp-fs-list-badge">{item.badge}</span>}
        </div>
      ))}
    </div>
  </div>
);

const BOOKING_ITEMS = [
  { icon: CalendarCheck, label: 'Bookings', mock: `${IMG}/renter/booking-mocks/smart-booking.png` },
  { icon: MessageSquareQuote, label: 'Quotes', mock: `${IMG}/renter/booking-mocks/guest-quotes.png` },
  { icon: Calendar, label: 'Calendar', mock: `${IMG}/renter/booking-mocks/booking-calendar.png` },
  { icon: FileText, label: 'Agreements', mock: `${IMG}/renter/booking-mocks/rental-agreement.png` },
  { icon: Tag, label: 'Discounts', mock: `${IMG}/renter/booking-mocks/discount-codes.png` },
  { icon: ClipboardList, label: 'Policy/Rules', mock: `${IMG}/renter/booking-mocks/policy-rules.png` },
  { icon: CarFront, label: 'Fleet', mock: `${IMG}/renter/booking-mocks/fleet.png` },
  { icon: MessageSquare, label: 'Messaging', mock: `${IMG}/renter/booking-mocks/messaging.png` },
  { icon: Package, label: 'Extras', mock: `${IMG}/renter/booking-mocks/extras.png` },
  { icon: Globe, label: 'Custom Domain', mock: `${IMG}/renter/booking-mocks/custom-domain.png` },
  { icon: UserCheck, label: 'Approvals', mock: `${IMG}/renter/booking-mocks/approvals.png` },
];

const TRUST_ITEMS = [
  { icon: ScanFace, label: 'Identity', mock: `${IMG}/renter/verify-mocks/identity.png` },
  { icon: IdCard, label: 'License', mock: `${IMG}/renter/verify-mocks/license.png` },
  { icon: ShieldAlert, label: 'Criminal', mock: `${IMG}/renter/verify-mocks/criminal.png` },
  { icon: Car, label: 'Driving', mock: `${IMG}/renter/verify-mocks/driving.png` },
  { icon: Ban, label: 'Blocklist', mock: `${IMG}/renter/verify-mocks/blocklist.png` },
];

/* ---------- Verify: checkmark benefits + colored capability pills ---------- */

const VERIFY_PILLS = [
  { icon: User, label: 'Identity Verification' },
  { icon: Search, label: 'Criminal Record' },
  { icon: FileText, label: 'Access MVR Data' },
  { icon: ShieldCheck, label: 'Insurance Validation' },
  { icon: BadgeCheck, label: 'License Status' },
  { icon: Camera, label: 'Selfie Biometrics' },
];

// Features that render as selectable pills with a swapping preview image.
const PILL_ITEMS = {
  book: BOOKING_ITEMS,
  verify: TRUST_ITEMS,
};

const FLEET_ITEMS = [
  { icon: Car, label: 'Fleet Management' },
  { icon: CarFront, label: 'Add Vehicles' },
  { icon: Radio, label: 'Telematics' },
  { icon: MessageSquare, label: 'Schedule Messages' },
  { icon: MessagesSquare, label: 'In-App Messaging' },
  { icon: ClipboardList, label: 'Digital Claims' },
  { icon: ReceiptText, label: 'Tolls & Tickets' },
  { icon: LifeBuoy, label: 'Roadside coverage' },
];

// Automated workflows shown as a continuously cycling card list.
const WORKFLOW_ITEMS = [
  { icon: Calendar, label: 'Booking confirmed' },
  { icon: Car, label: 'Vehicle assigned' },
  { icon: BadgeCheck, label: 'Driver verified' },
  { icon: ShieldCheck, label: 'Insurance coverage activated' },
  { icon: FileText, label: 'Rental agreement sent' },
  { icon: Banknote, label: 'Security deposit collected' },
  { icon: MessagesSquare, label: 'Renter question answered' },
  { icon: CalendarClock, label: 'Pickup reminder scheduled' },
  { icon: Radio, label: 'Telematics synced' },
  { icon: TriangleAlert, label: 'Rental violation flagged' },
  { icon: ReceiptText, label: 'Toll & ticket charged back' },
  { icon: ClipboardList, label: 'Damage claim opened' },
  { icon: LifeBuoy, label: 'Roadside assistance dispatched' },
  { icon: FileText, label: 'Invoice generated' },
  { icon: Globe, label: 'Website listing published' },
];

const AutomationCards = ({ items }) => {
  // Rendered twice so the downward scroll loops seamlessly.
  const loop = [...items, ...items];
  return (
    <div className="ilp-marquee">
      <div className="ilp-marquee-track ilp-fs-auto-track" style={{ '--ilp-dur': '34s' }}>
        {loop.map((item, i) => (
          <div key={i} className="ilp-fs-auto-slot" aria-hidden={i >= items.length}>
            {/* gradient border via padded wrapper so corners stay rounded */}
            <div className="ilp-fs-auto-stroke">
              <div className="ilp-fs-auto-card">
                <span className="ilp-fs-auto-icon">
                  <item.icon className="ilp-fs-icon-20" />
                </span>
                <p className="ilp-fs-auto-label">{item.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Insure: two side-by-side coverage cards ---------- */

const PoweredByInshur = () => (
  <div className="ilp-fs-powered">
    Powered by <span className="ilp-shimmer-text ilp-fs-powered-mark">INSHUR</span>
  </div>
);

const CoverageCard = ({ title, dotColor, price, unit, bullets, baseDelay }) => (
  <div className="ilp-fs-cov">
    <div className="ilp-fs-cov-top">
      <div className="ilp-fs-cov-title-row">
        <span className="ilp-fs-cov-dot" style={{ backgroundColor: dotColor }} />
        <span className="ilp-fs-cov-title">{title}</span>
      </div>
      <PoweredByInshur />
    </div>
    <p className="ilp-fs-cov-eyebrow">Starting at</p>
    <p className="ilp-fs-cov-price-row">
      <span className="ilp-fs-cov-price">{price}</span>
      <span className="ilp-fs-cov-unit"> {unit}</span>
    </p>
    <div className="ilp-fs-cov-bullets">
      {bullets.map((b, i) => (
        <div
          key={b}
          className="ilp-step ilp-fs-cov-bullet"
          style={{ animationDelay: `${baseDelay + i * 0.5}s` }}
        >
          <span className="ilp-fs-cov-check" style={{ backgroundColor: dotColor }}>
            <Check className="ilp-fs-icon-10" />
          </span>
          <span className="ilp-fs-cov-bullet-text">{b}</span>
        </div>
      ))}
    </div>
  </div>
);

const InsureVisual = () => (
  <div className="ilp-fs-insure-grid">
    <CoverageCard
      title="Off-Rent Coverage"
      dotColor="#4338CA"
      price="$42"
      unit="/ month"
      bullets={['State-compliant program', 'State liability coverage', 'Physical damage coverage']}
      baseDelay={0.4}
    />
    <CoverageCard
      title="On-Rent Coverage"
      dotColor={BRAND_GREEN}
      price="$22"
      unit="/ rental day"
      bullets={['Liability + damage up to $90k', 'Primary rental coverage', 'Rideshare insurance included']}
      baseDelay={0.6}
    />
  </div>
);

/* ---------- Insure tab: RentShield plans panel (ported from insure-plans) ---------- */

const COVERAGE_PILLS = [
  { icon: ShieldCheck, label: 'Off Rent' },
  { icon: Car, label: 'On Rent' },
  { icon: Key, label: 'Leisure' },
  { icon: Users, label: 'Rideshare' },
  { icon: Package, label: 'Delivery' },
  { icon: Utensils, label: 'Food' },
];

const RENTSHIELD_PLANS = [
  {
    name: 'Off-Rent Coverage',
    price: '$40',
    unit: '/ month',
    description:
      'Billed monthly to keep your vehicle and business state compliant while your fleet sits between bookings and is used for business.',
    bullets: ['Billed monthly', 'Keeps your vehicle & business state compliant', 'Covers prep, moving & downtime'],
    footnote:
      '*Starting price reflects liability coverage only, per vehicle. Varies by state, vehicle, and operator profile. Subject to underwriting approval and policy terms.',
  },
  {
    name: 'On-Rent Coverage',
    price: '$16',
    unit: '/ rental day',
    description: 'Primary, A-rated commercial auto insurance billed only for the days a vehicle is actively rented.',
    bullets: [
      'Primary commercial auto coverage',
      'Liability + physical damage',
      'Covers your business, vehicle & driver',
    ],
    footnote:
      'Per-day rate is a starting estimate and varies by state, vehicle, and rental terms. Limits and eligibility subject to underwriting approval and policy terms.',
    featured: true,
  },
];

const RentShieldCard = ({ plan }) => (
  <div
    className="ilp-fs-plan"
    style={{
      borderColor: CORAL,
      boxShadow: plan.featured
        ? '0 20px 40px -20px rgba(255,75,92,0.35)'
        : '0 10px 30px -20px rgba(14,26,56,0.4)',
    }}
  >
    <h4 className="ilp-shimmer-text ilp-fs-plan-name" style={{ color: NAVY }}>
      {plan.name}
    </h4>
    <p className="ilp-fs-plan-eyebrow">Starting at</p>
    <div className="ilp-fs-plan-price-row">
      <span className="ilp-shimmer-text ilp-fs-plan-price" style={{ color: NAVY }}>
        {plan.price}
      </span>
      <span className="ilp-fs-plan-unit">{plan.unit}</span>
    </div>

    <p className="ilp-fs-plan-desc">{plan.description}</p>

    <ul className="ilp-fs-plan-bullets">
      {plan.bullets.map((b) => (
        <li key={b}>
          <span className="ilp-fs-plan-check" style={{ backgroundColor: GREEN }}>
            <Check className="ilp-fs-icon-10" strokeWidth={3} />
          </span>
          <span className="ilp-fs-plan-bullet-text">{b}</span>
        </li>
      ))}
    </ul>

    <p className="ilp-fs-plan-footnote">{plan.footnote}</p>
  </div>
);

const InsurePlans = ({ logo, logoAlt, title, subcopy }) => (
  <div className="ilp-fs-split">
    {/* Left: copy + coverage pills */}
    <div className="ilp-fs-col">
      {/* Logo lockup: RentShield + Powered by INSHUR */}
      <div className="ilp-fs-lockup">
        {logo ? <img src={logo} alt={logoAlt || ''} className="ilp-fs-lockup-logo" /> : null}
        <span aria-hidden="true" className="ilp-fs-lockup-rule" />
        <span className="ilp-fs-lockup-by">
          <span className="ilp-fs-lockup-by-text">Powered by</span>
          <img src={`${IMG}/logos/inshur.svg`} alt="INSHUR" className="ilp-fs-lockup-inshur" />
        </span>
      </div>

      <h3 className="ilp-fs-title">{title}</h3>
      <p className="ilp-fs-subcopy">{subcopy}</p>

      {/* Coverage pills — rent-tab style with icons */}
      <div className="ilp-fs-pills">
        {COVERAGE_PILLS.map((p) => (
          <span key={p.label} className="ilp-fs-pill ilp-fs-pill--navy">
            <p.icon className="ilp-fs-icon-16" />
            {p.label}
          </span>
        ))}
      </div>

      <div className="ilp-fs-cta-wrap">
        <ShimmerCta />
      </div>
    </div>

    {/* Right: RentShield coverage cards */}
    <div className="ilp-fs-plan-grid">
      {RENTSHIELD_PLANS.map((p) => (
        <RentShieldCard key={p.name} plan={p} />
      ))}
    </div>
  </div>
);

/* ---------- Resolve: one animated card per pill (appear + stay) ---------- */

const RESOLVE_STEPS = [
  { icon: Banknote, label: 'Issue refunds', status: 'Refund sent' },
  { icon: TriangleAlert, label: 'Rental violations', status: 'Billed' },
  { icon: ShieldCheck, label: 'Insurance claims', status: 'Claim filed' },
  { icon: ReceiptText, label: 'Tolls & Tickets collections', status: 'Billed' },
];

const ClaimVisual = () => (
  <div className="ilp-fs-claim">
    {RESOLVE_STEPS.map((step, i) => (
      <div
        key={step.label}
        className="ilp-step ilp-fs-claim-card"
        style={{ animationDelay: `${0.15 + i * 0.35}s` }}
      >
        <span className="ilp-fs-claim-icon">
          <step.icon className="ilp-fs-icon-20" />
        </span>
        <p className="ilp-fs-claim-label">{step.label}</p>
        <span
          className="ilp-step ilp-fs-claim-status"
          style={{ animationDelay: `${0.15 + i * 0.35 + 0.35}s` }}
        >
          <Check className="ilp-fs-icon-14" strokeWidth={3} />
          {step.status}
        </span>
      </div>
    ))}
  </div>
);

/* ---------- Pay: payment methods ---------- */

const PAY_APPS = [
  { src: `${IMG}/pay-logos/venmo.svg`, alt: 'Venmo' },
  { src: `${IMG}/pay-logos/cash-app.svg`, alt: 'Cash App' },
  { src: `${IMG}/pay-logos/zelle.svg`, alt: 'Zelle' },
  { src: `${IMG}/pay-logos/paypal.svg`, alt: 'PayPal' },
  { src: `${IMG}/pay-logos/google-pay.svg`, alt: 'Google Pay' },
  { src: `${IMG}/pay-logos/apple-pay.svg`, alt: 'Apple Pay' },
];

const PAY_METHODS = [
  { icon: Banknote, label: 'Cash' },
  { icon: CreditCard, label: 'Credit card' },
  { icon: Landmark, label: 'ACH' },
];

const PayVisual = () => (
  <div className="ilp-fs-pay">
    {/* core methods */}
    <div className="ilp-fs-pay-methods">
      {PAY_METHODS.map((m, i) => (
        <div key={m.label} className="ilp-step ilp-fs-pay-method" style={{ animationDelay: `${0.2 + i * 0.2}s` }}>
          <m.icon className="ilp-fs-icon-32" />
          <span className="ilp-fs-pay-method-label">{m.label}</span>
        </div>
      ))}
    </div>

    {/* app logos */}
    <div className="ilp-fs-pay-apps">
      {PAY_APPS.map((app, i) => (
        <div key={app.alt} className="ilp-step ilp-fs-pay-app" style={{ animationDelay: `${0.9 + i * 0.15}s` }}>
          <img
            src={app.src}
            alt={app.alt}
            className={`ilp-fs-pay-logo${i === 0 ? ' ilp-fs-pay-logo--round' : ''}`}
          />
        </div>
      ))}
    </div>
  </div>
);

/* ---------- Grow ---------- */

const EDUCATE_FORMATS = [
  { icon: Video, label: 'Watch' },
  { icon: BookOpen, label: 'Read' },
  { icon: Headphones, label: 'Listen' },
];

/* RentAcademy photos, reused from the /turo-alternative academy collage.
   Four tiles is what fits this panel without shrinking them to thumbnails. */
const ACADEMY_TILES = [
  {
    src: `${SHARED_IMG}/academy/studio-duo.webp`,
    alt: 'Two operators recording a rental business podcast episode together',
  },
  {
    src: `${SHARED_IMG}/academy/training-session.webp`,
    alt: 'Operator walking a teammate through fleet performance on a screen',
  },
  {
    src: `${SHARED_IMG}/academy/mic-smoke.webp`,
    alt: 'Studio microphone lit in warm and cool stage light',
  },
  {
    src: `${SHARED_IMG}/academy/expo-floor.webp`,
    alt: 'Busy industry conference floor filled with attendees',
  },
];

const EducateVisual = () => (
  <div className="ilp-fs-edu">
    <div className="ilp-fs-edu-collage">
      {ACADEMY_TILES.map((tile, i) => (
        <figure
          key={tile.src}
          className="ilp-step ilp-fs-edu-tile"
          style={{ animationDelay: `${0.1 + i * 0.12}s` }}
        >
          <img src={tile.src} alt={tile.alt} />
        </figure>
      ))}
    </div>
    <div className="ilp-fs-edu-formats">
      {EDUCATE_FORMATS.map((f, i) => (
        <div
          key={f.label}
          className="ilp-step ilp-fs-edu-format"
          style={{ animationDelay: `${0.6 + i * 0.12}s` }}
        >
          <f.icon className="ilp-fs-icon-20 ilp-fs-edu-format-icon" />
          <span className="ilp-fs-edu-format-label">{f.label}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ---------- Product walkthrough video ----------
   Portrait phone-frame clips, the same assets the /turo-alternative page
   uses (see src/Component/TuroAlternative/ProductBlocks.jsx). Muted, looped
   and inline so browsers allow autoplay; a click toggles sound. */

const ProductVideo = ({ src, caption }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <div className="ilp-fs-video">
      <div className="ilp-fs-phone">
        <span className="ilp-fs-phone-notch" aria-hidden="true" />
        <video
          ref={videoRef}
          className="ilp-fs-video-el"
          src={src}
          aria-label={caption}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      </div>
      <button
        type="button"
        onClick={toggleSound}
        className="ilp-fs-video-unmute"
        aria-label={muted ? 'Unmute video' : 'Mute video'}
      >
        {muted ? <VolumeX className="ilp-fs-icon-14" /> : <Volume2 className="ilp-fs-icon-14" />}
        {muted ? 'Unmute' : 'Mute'}
      </button>
    </div>
  );
};

/* ---------- Verify: custom panel with checkmarks, pills, and video ---------- */

const VerifyContent = ({ logo, logoAlt, title, subcopy }) => (
  <div className="ilp-fs-split ilp-fs-split--center">
    {/* Copy */}
    <div className="ilp-fs-col">
      <img src={logo} alt={logoAlt} className="ilp-fs-logo" />
      <h3 className="ilp-fs-title ilp-fs-title--pre">{title}</h3>
      <p className="ilp-fs-subcopy">{subcopy}</p>

      {/* Capability pills */}
      <div className="ilp-fs-pills">
        {VERIFY_PILLS.map((p) => (
          <div key={p.label} className="ilp-fs-pill ilp-fs-pill--navy">
            <p.icon className="ilp-fs-icon-16" />
            <span>{p.label}</span>
          </div>
        ))}
      </div>

      <ShimmerCta />
    </div>

    {/* Video placeholder */}
    <div className="ilp-fs-col-full">
      <ProductVideo src={RENTID_VIDEO} caption="RentID verification walkthrough" />
    </div>
  </div>
);

/* ---------- Rent: capability checklist + video placeholder ---------- */

// Row-major order so the 2-column grid reads: left column top-to-bottom, then right.
const BOOK_CHECKS = [
  'Booking engine',
  'Fleet management',
  'Driver verification',
  'Embedded insurance',
  'Automated back office',
  'Custom agreements',
  'Rental violations',
  'Online claims',
  'AI rental agents',
  'Free website',
];

const BookContent = ({ logo, logoAlt, title, subcopy }) => (
  <div className="ilp-fs-split ilp-fs-split--center">
    {/* Copy */}
    <div className="ilp-fs-col">
      <img src={logo} alt={logoAlt} className="ilp-fs-logo" />
      <h3 className="ilp-fs-title ilp-fs-title--pre">{title}</h3>
      <p className="ilp-fs-subcopy">{subcopy}</p>

      {/* Capability checklist */}
      <ul className="ilp-fs-checks">
        {BOOK_CHECKS.map((c) => (
          <li key={c}>
            <Check className="ilp-fs-icon-16 ilp-fs-check-mark" strokeWidth={3} aria-hidden="true" />
            <span>{c}</span>
          </li>
        ))}
      </ul>

      <ShimmerCta />
    </div>

    {/* Video placeholder */}
    <div className="ilp-fs-col-full">
      <ProductVideo src={RENTDIRECT_VIDEO} caption="RentDirect platform walkthrough" />
    </div>
  </div>
);

const FeatureVisual = ({ id }) => {
  switch (id) {
    case 'book':
      return <ListVisual heading="Booking & Commerce" items={BOOKING_ITEMS} />;
    case 'verify':
      return <ListVisual heading="Trust & Safety" items={TRUST_ITEMS} />;
    case 'insure':
      return <InsureVisual />;
    case 'manage':
      return <ListVisual heading="Fleet & Operations" items={FLEET_ITEMS} />;
    case 'pay':
      return <PayVisual />;
    case 'claim':
      return <ClaimVisual />;
    case 'educate':
      return <EducateVisual />;
    default:
      return null;
  }
};

const MANAGE_PHASE_PILLS = ['Pre-trip', 'On-trip', 'Post-trip'];
const MANAGE_TASK_PILLS = ['Operational', 'Logistical', 'Administrative'];
const PAY_PILLS = ['Ecommerce payments', 'Pay at Counter', 'Reservation deposits', 'Pre-authorization holds'];
const CLAIM_PILLS = ['Issue refunds', 'Rental violations', 'Insurance claims', 'Tolls & Tickets collections'];
const EDUCATE_PILLS = [
  'Gig economy',
  'Marketplace hosts',
  'Digital rental',
  'Rideshare',
  'Traditional rental',
  'Franchise owners',
];

/** Tabbed platform capability showcase — 7 tabs, each with its own visual. */
const FeatureShowcase = ({ showHeader = true, className = '' }) => {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [pillSelected, setPillSelected] = useState(0);
  const current = FEATURES[active];
  const pillItems = PILL_ITEMS[current.id];

  // Manual tab selection only — no auto-cycling. Reset pill selection per tab.
  const handleSelect = useCallback((i) => {
    setActive(i);
    setPillSelected(0);
  }, []);

  // Selecting a pill swaps the preview image.
  const handlePillSelect = useCallback((i) => {
    setPillSelected(i);
  }, []);

  const plainPills =
    (current.id === 'pay' && PAY_PILLS) ||
    (current.id === 'claim' && CLAIM_PILLS) ||
    (current.id === 'educate' && EDUCATE_PILLS) ||
    null;

  return (
    <section className={`ilp-fs${className ? ` ${className}` : ''}`}>
      {showHeader && (
        <div className="ilp-fs-head">
          <p className="ilp-fs-eyebrow">Modern rental platform</p>
          <h2 className="ilp-fs-heading">Everything to manage your rental business, all in one place.</h2>
        </div>
      )}

      {/* Horizontal tab bar — scrollable on mobile/tablet, spaced out on desktop */}
      <div className="ilp-fs-tabbar">
        <ul role="tablist" aria-label="Rental platform capabilities" className="ilp-noscrollbar ilp-fs-tablist">
          {FEATURES.map((f, i) => {
            const isActive = i === active;
            const isHot = isActive || hovered === i;
            return (
              <li key={f.id} className="ilp-fs-tab-li">
                <button
                  type="button"
                  role="tab"
                  onClick={() => handleSelect(i)}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  aria-selected={isActive}
                  className="ilp-fs-tab"
                >
                  <span className="ilp-fs-tab-inner">
                    <span
                      aria-hidden="true"
                      className={`ilp-fs-tab-swipe${isActive ? ' is-on' : ''}`}
                    />
                    <span className={`ilp-fs-tab-label${isHot ? ' is-hot' : ''}`}>{f.label}</span>
                  </span>
                  {/* active underline indicator on the tab bar baseline */}
                  <span aria-hidden="true" className={`ilp-fs-tab-underline${isActive ? ' is-on' : ''}`} />
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Panel: title + subcopy on one side, animation on the other (stacks on mobile) */}
      <div>
        {current.id === 'book' ? (
          <div key={`book-${active}`} className="ilp-fade-slide">
            <BookContent
              logo={current.logo}
              logoAlt={current.logoAlt}
              title={current.title}
              subcopy={current.subcopy}
            />
          </div>
        ) : current.id === 'insure' ? (
          <div key={`insure-${active}`} className="ilp-fade-slide">
            <InsurePlans
              logo={current.logo}
              logoAlt={current.logoAlt}
              title={current.title}
              subcopy={current.subcopy}
            />
          </div>
        ) : current.id === 'verify' ? (
          <div key={`verify-${active}`} className="ilp-fade-slide">
            <VerifyContent
              logo={current.logo}
              logoAlt={current.logoAlt}
              title={current.title}
              subcopy={current.subcopy}
            />
          </div>
        ) : (
          <div
            className={`ilp-fs-split ilp-fs-split--${
              current.id === 'manage' || current.id === 'claim' || current.id === 'educate' ? 'start' : 'center'
            }`}
          >
            {/* Copy */}
            <div key={`text-${active}`} className="ilp-fade-slide ilp-fs-col">
              {current.logo ? (
                <img src={current.logo} alt={current.logoAlt} className="ilp-fs-logo" />
              ) : (
                <div className="ilp-fs-logo-fallback">
                  <CreditCard className="ilp-fs-icon-28" />
                  <span>Payment Processing</span>
                </div>
              )}
              <h3 className="ilp-fs-title ilp-fs-title--pre">{current.title}</h3>
              <p className={`ilp-fs-subcopy${current.id === 'pay' ? ' ilp-fs-subcopy--strong' : ''}`}>
                {current.subcopy}
              </p>

              {current.id === 'manage' && (
                <div className="ilp-fs-pills">
                  {MANAGE_PHASE_PILLS.map((label) => (
                    <span key={label} className="ilp-fs-pill">
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {current.id === 'manage' && (
                <div className="ilp-fs-tasks">
                  <p className="ilp-fs-tasks-head">Tasks Managed</p>
                  <div className="ilp-fs-pills ilp-fs-pills--flush">
                    {MANAGE_TASK_PILLS.map((label) => (
                      <span key={label} className="ilp-fs-pill">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {plainPills && (
                <div className="ilp-fs-pills">
                  {plainPills.map((label) => (
                    <span key={label} className="ilp-fs-pill">
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {pillItems && (
                <div className="ilp-fs-pills">
                  {pillItems.map((item, i) => {
                    const selected = i === pillSelected;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => handlePillSelect(i)}
                        aria-pressed={selected}
                        className={`ilp-fs-pill ilp-fs-pill--btn${selected ? ' is-selected' : ''}`}
                      >
                        {/* Collapsed to zero width until selected, then it grows the tick in */}
                        <span aria-hidden="true" className="ilp-fs-pill-tick-slot">
                          <span className="ilp-fs-pill-tick">
                            <Check className="ilp-fs-icon-12" strokeWidth={3.5} />
                          </span>
                        </span>
                        <span className="ilp-fs-pill-text">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* key replays the shimmer sweep whenever the pill selection changes */}
              <ShimmerCta key={`cta-${current.id}-${pillSelected}`} />
            </div>

            {/* Animation */}
            <div className="ilp-fs-col-full">
              {current.id === 'manage' ? (
                /* Cycling automation cards — matched to the shared visual height */
                <div className="ilp-fs-stage">
                  <AutomationCards items={WORKFLOW_ITEMS} />
                </div>
              ) : pillItems ? (
                <div className="ilp-fs-preview">
                  <img
                    key={pillSelected}
                    src={pillItems[pillSelected].mock}
                    alt={`${pillItems[pillSelected].label} preview`}
                    className="ilp-fade-slide ilp-fs-preview-img"
                  />
                </div>
              ) : (
                /* key forces remount so the storyboard restarts on each change */
                <div className="ilp-fs-stage ilp-fs-stage--fill">
                  <FeatureVisual key={active} id={current.id} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureShowcase;
